import { useQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { CompanionCard } from '../../../components/companion-card';
import { AlertContext } from '../../../contexts/alert-context';
import { makeCompanionBusyPredicate, type ReceivingSession } from '../../../data/receiving';
import type { Companion, UploaderConnection } from '../../../data/uploader-connection';
import { receivingSessionsQueryOptions } from '../../../query/query-options';
import type { ReceivingService } from '../../../services/receiving';
import { StepHeading } from './step-heading';

type CompanionPickerStepProps = {
  connections: UploaderConnection[];
  storeId: string;
  sessionId: string;
  disabled: boolean;
  service: ReceivingService;
};

export function CompanionPickerStep({
  connections,
  storeId,
  sessionId,
  disabled,
  service,
}: CompanionPickerStepProps) {
  const [connecting, setConnecting] = useState<string | null>(null);
  const { addErrorAlert } = useContext(AlertContext);

  const { data: sessions = [] } = useQuery<ReceivingSession[]>({
    ...receivingSessionsQueryOptions(storeId),
    queryFn: () => service.listActiveSessions(storeId),
  });

  const isCompanionBusy = makeCompanionBusyPredicate(sessions);

  const companions = connections.filter((c): c is Companion => Boolean(c.uploader?.machine_id));

  const ordered = companions
    .map((conn) => ({
      conn,
      busy: isCompanionBusy(conn),
    }))
    .sort((a, b) => Number(a.busy) - Number(b.busy));

  const handlePick = (machineId: string) => {
    if (disabled || connecting) {
      return;
    }

    setConnecting(machineId);
    void service.setCompanion(storeId, sessionId, machineId).catch((err: unknown) => {
      setConnecting(null);

      // Surface the backend's reason (e.g. "companion already has an active
      // receiving session") instead of silently no-op'ing the click. The
      // fetch wrapper prefixes thrown errors with "Invalid response:" — strip
      // it so the toast reads as a plain sentence for the store owner.
      const reason = err instanceof Error ? err.message.replace(/^Invalid response:\s*/, '') : '';
      addErrorAlert(
        reason
          ? reason.charAt(0).toUpperCase() + reason.slice(1)
          : "Couldn't select this companion. Please refresh and try again."
      );
    });
  };

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading>Select a Companion</StepHeading>
      {ordered.length === 0 ? (
        <p className="text-sm text-gray-500">
          No Companion computers are online. Open the Orderbot Companion app on a store computer to
          continue.
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {ordered.map(({ conn, busy }) => {
            const isConnecting = connecting === conn.uploader.machine_id;

            return (
              <CompanionCard
                key={conn.connection_id}
                connection={conn}
                busy={busy}
                isActive={isConnecting}
                onClick={
                  !busy && !disabled && !connecting
                    ? () => handlePick(conn.uploader.machine_id)
                    : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
